
import React, { useState, useEffect } from "react";
import { Layout } from "@/components/layout/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useProfileData } from "./hooks/useProfileData";
import { useProfileFormState } from "./hooks/useProfileFormState";
import PanelHeader from "./components/PanelHeader";
import PanelTabs from "./components/PanelTabs";
import PanelLoading from "./components/PanelLoading";
import LoginPrompt from "./components/LoginPrompt";

interface LocationState {
  activeTab?: string;
}

const CreatorPanel = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as LocationState | null;
  
  // Get tab from URL params or state
  const searchParams = new URLSearchParams(location.search);
  const tabFromUrl = searchParams.get('tab');
  const initialTab = tabFromUrl || locationState?.activeTab || "profile";
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const { profile, isLoading } = useProfileData();
  const { profileFormState, handleFormStateChange } = useProfileFormState(profile);

  // Listen for changes in URL search params to update the active tab
  useEffect(() => {
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl, location.search]);

  // Update URL when tab changes
  const handleTabChange = (value: string) => {
    if (value === activeTab) return; // Prevent unnecessary navigation if tab is the same
    
    setActiveTab(value);
    navigate(`/creator/panel?tab=${value}`, { replace: true });
  };

  if (!user) {
    return (
      <Layout>
        <LoginPrompt />
      </Layout>
    );
  }

  if (isLoading) {
    return (
      <Layout>
        <PanelLoading />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6 md:py-8">
        <PanelHeader />
        
        <PanelTabs 
          activeTab={activeTab}
          handleTabChange={handleTabChange}
          profileFormState={profileFormState}
          profile={profile}
          onFormStateChange={handleFormStateChange}
        />
      </div>
    </Layout>
  );
};

export default CreatorPanel;
